import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, Shield, Trophy, ChevronDown, Star, Calculator, ListChecks, Calendar, CheckCircle2, Lock, CreditCard, X } from 'lucide-react';
// --- Meta Pixel Component ---
const MetaPixel = () => {
  useEffect(() => {
    const pixelId = '1252242806993118';
    const w = window as any;
    if (w.fbq) {
      try {
        w.fbq('track', 'PageView');
      } catch (e) {
        // ignore
      }
      return;
    }
    // Initialize fbq queue manually (avoids the original snippet's reliance on an existing <script> tag)
    const n: any = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    w.fbq = n;
    if (!w._fbq) w._fbq = n;
    // Append the script directly to <head> instead of relying on an existing <script> tag.
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onerror = () => {
      // Silently fail in sandboxed/preview environments where the pixel may be blocked.
    };
    const target = document.head || document.body || document.documentElement;
    if (target) {
      target.appendChild(script);
    }
    try {
      w.fbq('init', pixelId);
      w.fbq('track', 'PageView');
    } catch (e) {
      // ignore
    }
  }, []);
  return null;
};
// --- Fake Purchase Popup Component ---
const names = ['Martín', 'Carolina', 'Federico', 'Lucía', 'Diego', 'Valentina', 'Joaquín', 'Florencia', 'Tomás', 'Camila'];
const cities = ['Buenos Aires', 'Rosario', 'Córdoba', 'Mendoza', 'La Plata', 'Mar del Plata', 'Tucumán', 'Salta', 'Neuquén', 'Santa Fe'];
const PurchasePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    name: '',
    city: '',
    time: ''
  });
  useEffect(() => {
    const showPopup = () => {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCity = cities[Math.floor(Math.random() * cities.length)];
      const randomTime = Math.floor(Math.random() * 15) + 1;
      setPurchaseData({
        name: randomName,
        city: randomCity,
        time: `${randomTime} min`
      });
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };
    const initialDelay = setTimeout(showPopup, 10000);
    const interval = setInterval(showPopup, 35000);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);
  return <AnimatePresence data-id="element-0">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: '-50%'
    }} animate={{
      opacity: 1,
      y: 0,
      x: '-50%'
    }} exit={{
      opacity: 0,
      y: 20,
      x: '-50%'
    }} className="fixed bottom-4 left-1/2 z-50 w-[90%] max-w-sm bg-hyrox-card border border-hyrox-border rounded-lg p-4 shadow-2xl flex items-center gap-4" data-id="element-1">
          <div className="bg-hyrox-neon/20 p-2 rounded-full" data-id="element-2">
            <CheckCircle2 className="w-6 h-6 text-hyrox-neon" data-id="element-3" />
          </div>
          <div className="flex-1" data-id="element-4">
            <p className="text-sm text-gray-300" data-id="element-5">
              <span className="font-bold text-white" data-id="element-6">{purchaseData.name}</span>{' '}
              de {purchaseData.city}
            </p>
            <p className="text-xs text-hyrox-neon font-medium" data-id="element-7">
              Compró Hyrox Pro System hace {purchaseData.time}
            </p>
          </div>
          <button onClick={() => setIsVisible(false)} className="text-gray-500 hover:text-white" data-id="element-8">
            <X className="w-4 h-4" data-id="element-9" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
// --- Countdown Component ---
const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return {
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (num: number) => num.toString().padStart(2, '0');
  return <div className="flex gap-2 text-3xl md:text-5xl font-display text-hyrox-neon justify-center" data-id="element-10">
      <div className="flex flex-col items-center bg-hyrox-card px-4 py-2 rounded border border-hyrox-border" data-id="element-11">
        <span data-id="element-12">{pad(timeLeft.hours)}</span>
        <span className="text-xs font-sans text-gray-400 uppercase tracking-wider" data-id="element-13">
          Horas
        </span>
      </div>
      <span className="mt-2" data-id="element-14">:</span>
      <div className="flex flex-col items-center bg-hyrox-card px-4 py-2 rounded border border-hyrox-border" data-id="element-15">
        <span data-id="element-16">{pad(timeLeft.minutes)}</span>
        <span className="text-xs font-sans text-gray-400 uppercase tracking-wider" data-id="element-17">
          Min
        </span>
      </div>
      <span className="mt-2" data-id="element-18">:</span>
      <div className="flex flex-col items-center bg-hyrox-card px-4 py-2 rounded border border-hyrox-border" data-id="element-19">
        <span data-id="element-20">{pad(timeLeft.seconds)}</span>
        <span className="text-xs font-sans text-gray-400 uppercase tracking-wider" data-id="element-21">
          Seg
        </span>
      </div>
    </div>;
};
// --- Accordion Component ---
const AccordionItem = ({
  num,
  title,
  subtitle,
  details
}: {
  num: string;
  title: string;
  subtitle: string;
  details: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-hyrox-border bg-hyrox-card rounded-lg overflow-hidden mb-4" data-id="element-22">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-white/5 transition-colors" data-id="element-23">
        <div className="flex items-center gap-4 md:gap-6" data-id="element-24">
          <span className="font-display text-4xl md:text-5xl text-hyrox-neon opacity-80" data-id="element-25">
            {num}
          </span>
          <div data-id="element-26">
            <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide" data-id="element-27">
              {title}
            </h3>
          </div>
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-28">
          <ChevronDown className="w-6 h-6 text-hyrox-neon" data-id="element-29" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-30">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-31">
            <div className="p-4 md:p-6 pt-0 border-t border-hyrox-border/50 text-gray-300" data-id="element-32">
              <p className="font-medium text-white mb-2" data-id="element-33">{subtitle}</p>
              <p className="text-sm text-gray-400" data-id="element-34">{details}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function App() {
  useEffect(() => {
    document.title = '[Hyrox Pro System]';
  }, []);
  const scrollToCheckout = () => {
    document.getElementById('comprar')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const checkoutUrl = 'https://checkout.ebookpoint.shop/cart/59513969934417:1';
  const tomos = [{
    num: '01',
    title: 'Qué es HYROX y por qué lo cambia todo',
    subtitle: 'Fundamentos, historia y sistema de competencia',
    details: '~50 págs · Capítulos 1–6'
  }, {
    num: '02',
    title: 'Programa de entrenamiento: 0 a HYROX en 16 semanas',
    subtitle: 'Periodización, bloques y microciclos',
    details: '~50 págs · Capítulos 7–12'
  }, {
    num: '03',
    title: 'Técnica profunda: las 8 estaciones',
    subtitle: 'Biomecánica, errores y correcciones',
    details: '~50 págs · Capítulos 13–18'
  }, {
    num: '04',
    title: 'Nutrición y recuperación para HYROX',
    subtitle: 'Fueling, timing y regeneración muscular',
    details: '~50 págs · Capítulos 19–24'
  }, {
    num: '05',
    title: 'Estrategia de carrera y mentalidad competitiva',
    subtitle: 'Pacing, splits y psicología del rendimiento',
    details: '~50 págs · Capítulos 25–30'
  }, {
    num: '06',
    title: 'HYROX para todos: niveles, edades y objetivos',
    subtitle: 'Adaptaciones, comunidad y largo plazo',
    details: '~50 págs · Capítulos 31–36'
  }];
  const testimonials = [{
    name: 'Martín G.',
    age: 34,
    city: 'Buenos Aires',
    quote: 'Llegué al primer HYROX sin saber qué esperar. Con este sistema completé las 8 estaciones sin colapsar. Increíble el plan de 16 semanas.'
  }, {
    name: 'Carolina M.',
    age: 29,
    city: 'Rosario',
    quote: 'Me encantó cómo explica las técnicas de cada estación. El SkiErg ya no me destruye.'
  }, {
    name: 'Fede A.',
    age: 41,
    city: 'Córdoba',
    quote: 'Soy padre, trabajo 10hs y entreno 4 veces por semana. El plan se adapta perfecto a mi semana real.'
  }, {
    name: 'Lucía P.',
    age: 26,
    city: 'Mendoza',
    quote: 'La parte de nutrición y race day me cambió todo. Bajé mi tiempo 18 minutos en mi segundo HYROX.'
  }, {
    name: 'Diego F.',
    age: 38,
    city: 'La Plata',
    quote: 'Pasé de no poder hacer 10 burpees a terminar HYROX Pro. El mejor entrenamiento que compré online.'
  }];
  return <div className="min-h-screen bg-hyrox-dark selection:bg-hyrox-neon selection:text-black pb-20" data-id="element-35">
      <MetaPixel data-id="element-36" />
      <PurchasePopup data-id="element-37" />

      {/* 1. HERO SECTION */}
      <section className="pt-12 pb-16 px-4 max-w-5xl mx-auto text-center" data-id="element-38">
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] tracking-tight mb-8 max-w-4xl mx-auto" data-id="element-39">
          EMPEZA HYROX Y{' '}
          <span className="text-hyrox-neon" data-id="element-40">ENTRENA COMO UN PRO</span> DE 12 A
          16 SEMANAS
        </h1>

        <div className="relative max-w-3xl mx-auto mb-8" data-id="element-41">
          <div className="absolute inset-0 bg-hyrox-neon/20 blur-[100px] rounded-full -z-10" data-id="element-42"></div>
          <img src="https://cdn.magicpatterns.com/uploads/x1BQVeKBYv9Ku3HapmXN2S/ChatGPT_Image_May_11__2026__11_04_32_PM-removebg-preview.png" alt="Hyrox Pro System Bundle" className="w-[70%] h-auto mx-auto drop-shadow-2xl" data-id="element-43" />
        </div>

        <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8 font-medium" data-id="element-44">
          El sistema completo para principiantes que quieren empezar HYROX con
          un plan profesional, sin lesiones y con resultados reales.
        </p>

        <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-45">
          {['PDF digital', 'Acceso inmediato', 'Todos los niveles', '6 Tomos exclusivos'].map((pill, i) => <span key={i} className="px-4 py-1.5 rounded-full border border-hyrox-border bg-hyrox-card text-sm font-medium text-gray-300" data-id="element-46">
              {pill}
            </span>)}
        </div>

        <button onClick={scrollToCheckout} className="w-full max-w-md mx-auto bg-hyrox-neon hover:bg-hyrox-neonHover text-black font-display text-xl md:text-2xl py-4 px-8 rounded-lg uppercase tracking-wide transition-all transform hover:scale-105 shadow-[0_0_30px_rgba(196,255,0,0.3)] flex items-center justify-center gap-3" data-id="element-47">
          <span style={{
          filter: 'grayscale(1) brightness(0) invert(1)',
          opacity: 0.92
        }} data-id="element-48">
            💪
          </span>
          <span className="mt-1" data-id="element-49">QUIERO EMPEZAR MI TRANSFORMACIÓN</span>
        </button>

        <div className="flex flex-wrap justify-center items-center gap-4 mt-6 text-sm text-gray-400" data-id="element-50">
          <span className="flex items-center gap-1" data-id="element-51">
            <Shield className="w-4 h-4" data-id="element-52" /> Garantía 7 días
          </span>
          <span data-id="element-53">·</span>
          <span className="flex items-center gap-1" data-id="element-54">
            <Lock className="w-4 h-4" data-id="element-55" /> Pago 100% seguro
          </span>
          <span data-id="element-56">·</span>
          <span className="flex items-center gap-1" data-id="element-57">
            <CreditCard className="w-4 h-4" data-id="element-58" /> Mercado Pago · Tarjeta
          </span>
        </div>
      </section>

      {/* 2. COUNTDOWN SECTION */}
      <section className="bg-hyrox-neon text-black py-8 border-y-4 border-white" data-id="element-59">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-60">
          <h2 className="font-display text-3xl md:text-4xl uppercase mb-6 tracking-wide" data-id="element-61">
            OFERTA POR TIEMPO LIMITADO
          </h2>
          <Countdown data-id="element-62" />
          <button onClick={scrollToCheckout} className="mt-8 bg-black text-hyrox-neon font-display text-xl py-3 px-8 rounded uppercase tracking-wide hover:bg-gray-900 transition-colors" data-id="element-63">
            Aprovechar descuento ahora
          </button>
        </div>
      </section>

      {/* 3. VALUE PROP SECTION */}
      <section className="py-20 px-4 max-w-5xl mx-auto" data-id="element-64">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-65">
          <div data-id="element-66">
            <h2 className="font-display text-4xl md:text-5xl uppercase mb-6 leading-tight" data-id="element-67">
              El fitness funcional{' '}
              <span className="text-hyrox-neon" data-id="element-68">está cambiando.</span>
            </h2>
            <div className="space-y-4 text-gray-300 text-lg" data-id="element-69">
              <p data-id="element-70">
                La mayoría de los principiantes cometen el error de entrenar
                como si fuera CrossFit o salir a correr sin más.{' '}
                <strong data-id="element-71">HYROX requiere una estrategia específica.</strong>
              </p>
              <p data-id="element-72">
                Este sistema elimina la confusión. Te damos el paso a paso
                exacto, desde cómo respirar en el SkiErg hasta qué comer 72hs
                antes de la carrera.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-73">
            {[{
            icon: Target,
            title: 'Estrategia Clara',
            desc: 'Sabe exactamente qué hacer cada día.'
          }, {
            icon: Shield,
            title: 'Cero Lesiones',
            desc: 'Técnica perfecta para las 8 estaciones.'
          }, {
            icon: TrendingUp,
            title: 'Progresión Real',
            desc: 'De 0 a cruzar la meta en 16 semanas.'
          }, {
            icon: Trophy,
            title: 'Mentalidad',
            desc: 'Gestión del pacing y el dolor en carrera.'
          }].map((card, i) => <div key={i} className="bg-hyrox-card border border-hyrox-border p-6 rounded-xl" data-id="element-74">
                <card.icon className="w-10 h-10 text-hyrox-neon mb-4" data-id="element-75" />
                <h3 className="font-display text-xl uppercase mb-2" data-id="element-76">
                  {card.title}
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-77">{card.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. CONTENIDO / TOMOS */}
      <section className="py-20 px-4 bg-hyrox-card/50 border-y border-hyrox-border" data-id="element-78">
        <div className="max-w-3xl mx-auto" data-id="element-79">
          <h2 className="font-display text-4xl md:text-5xl uppercase text-center mb-12" data-id="element-80">
            CONTENIDO DEL <span className="text-hyrox-neon" data-id="element-81">SISTEMA</span>
          </h2>
          <div className="space-y-2" data-id="element-82">
            {tomos.map((tomo, i) => <AccordionItem key={i} {...tomo} data-id="element-83" />)}
          </div>
        </div>
      </section>

      {/* 5. TESTIMONIOS */}
      <section className="py-20 px-4 max-w-6xl mx-auto" data-id="element-84">
        <h2 className="font-display text-4xl md:text-5xl uppercase text-center mb-12" data-id="element-85">
          RESULTADOS <span className="text-hyrox-neon" data-id="element-86">REALES</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-87">
          {testimonials.map((test, i) => <div key={i} className="bg-hyrox-card border border-hyrox-border p-6 rounded-xl flex flex-col h-full" data-id="element-88">
              <div className="flex items-center gap-1 mb-4" data-id="element-89">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-hyrox-neon text-hyrox-neon" data-id="element-90" />)}
              </div>
              <p className="text-gray-300 mb-6 flex-grow italic" data-id="element-91">
                "{test.quote}"
              </p>
              <div className="flex items-center gap-4 mt-auto" data-id="element-92">
                <div className="w-12 h-12 rounded-full bg-hyrox-border flex items-center justify-center font-display text-xl text-hyrox-neon" data-id="element-93">
                  {test.name.charAt(0)}
                </div>
                <div data-id="element-94">
                  <p className="font-bold" data-id="element-95">{test.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-96">
                    {test.age} años · {test.city}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* 6. BONOS */}
      <section className="py-20 px-4 bg-hyrox-neon text-black" data-id="element-97">
        <div className="max-w-5xl mx-auto" data-id="element-98">
          <h2 className="font-display text-4xl md:text-6xl uppercase text-center mb-12 leading-none" data-id="element-99">
            BONOS EXCLUSIVOS
            <br data-id="element-100" />
            <span className="text-black/70" data-id="element-101">GRATIS POR HOY</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-102">
            {[{
            icon: Calculator,
            title: 'Bono 1: Calculadora de Splits',
            desc: 'Calculá tu pace ideal por estación según tu nivel',
            val: '$4.990'
          }, {
            icon: ListChecks,
            title: 'Bono 2: Checklist pre competencia',
            desc: 'Todo lo que necesitás antes, durante y después de la carrera',
            val: '$2.990'
          }, {
            icon: Calendar,
            title: 'Bono 3: Plan de 16 semanas',
            desc: "Programa día a día completo 'De 0 a cruzar la meta'",
            val: '$9.990'
          }].map((bono, i) => <div key={i} className="bg-black text-white p-8 rounded-xl border-2 border-black/10 relative overflow-hidden group" data-id="element-103">
                <div className="absolute top-4 right-4 bg-hyrox-neon text-black text-xs font-bold px-3 py-1 rounded-full uppercase" data-id="element-104">
                  Gratis Hoy
                </div>
                <bono.icon className="w-12 h-12 text-hyrox-neon mb-6" data-id="element-105" />
                <h3 className="font-display text-2xl uppercase mb-3" data-id="element-106">
                  {bono.title}
                </h3>
                <p className="text-gray-400 mb-6" data-id="element-107">{bono.desc}</p>
                <div className="mt-auto pt-4 border-t border-white/10" data-id="element-108">
                  <p className="text-sm text-gray-500" data-id="element-109">
                    Valor normal:{' '}
                    <span className="line-through" data-id="element-110">{bono.val}</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. PURCHASE SECTION */}
      <section id="comprar" className="py-24 px-4 max-w-4xl mx-auto text-center" data-id="element-111">
        <div className="bg-hyrox-card border-2 border-hyrox-neon rounded-2xl p-8 md:p-12 relative shadow-[0_0_50px_rgba(196,255,0,0.1)]" data-id="element-112">
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-hyrox-neon text-black font-display text-xl px-6 py-2 rounded-full uppercase tracking-wide whitespace-nowrap" data-id="element-113">
            Oferta Especial
          </div>

          <h2 className="font-display text-4xl md:text-5xl uppercase mb-8 mt-4" data-id="element-114">
            ACCESO INMEDIATO AL SISTEMA COMPLETO
          </h2>

          <div className="flex flex-col items-center justify-center mb-10" data-id="element-115">
            <p className="text-gray-500 text-2xl line-through font-display mb-2" data-id="element-116">
              $52.690
            </p>
            <div className="flex items-start gap-2 text-hyrox-neon" data-id="element-117">
              <span className="text-4xl font-display mt-2" data-id="element-118">$</span>
              <span className="text-7xl md:text-8xl font-display leading-none" data-id="element-119">
                19.990
              </span>
              <span className="text-xl font-display mt-2" data-id="element-120">ARS</span>
            </div>
            <p className="text-hyrox-neon/80 font-medium mt-4 bg-hyrox-neon/10 px-4 py-1 rounded-full" data-id="element-121">
              Único pago. Acceso de por vida.
            </p>
          </div>

          <a href={checkoutUrl} className="block w-full bg-hyrox-neon hover:bg-hyrox-neonHover text-black font-display text-2xl md:text-3xl py-6 px-8 rounded-xl uppercase tracking-wide transition-all transform hover:scale-105 shadow-[0_0_30px_rgba(196,255,0,0.3)] mb-6" data-id="element-122">
            COMPRAR AHORA Y EMPEZAR
          </a>

          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-400 mb-8" data-id="element-123">
            <span className="flex items-center gap-2" data-id="element-124">
              <Shield className="w-5 h-5 text-hyrox-neon" data-id="element-125" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-2" data-id="element-126">
              <Lock className="w-5 h-5 text-hyrox-neon" data-id="element-127" /> Pago 100% seguro
            </span>
          </div>

          <div className="border-t border-hyrox-border pt-8 mt-8" data-id="element-128">
            <h4 className="font-display text-xl uppercase text-hyrox-neon mb-4" data-id="element-129">
              🎁 + BONOS EXTRA EXCLUSIVOS POR HOY:
            </h4>
            <ul className="text-left max-w-md mx-auto space-y-3" data-id="element-130">
              <li className="flex items-start gap-3" data-id="element-131">
                <CheckCircle2 className="w-6 h-6 text-hyrox-neon shrink-0" data-id="element-132" />
                <span className="text-gray-300" data-id="element-133">
                  Calculadora de Proteínas e Hidratación + horas de sueño
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-134">
                <CheckCircle2 className="w-6 h-6 text-hyrox-neon shrink-0" data-id="element-135" />
                <span className="text-gray-300" data-id="element-136">
                  Guía de suplementos con respaldo científico
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 text-center max-w-3xl mx-auto px-4" data-id="element-137">
          <p className="font-display text-2xl md:text-4xl uppercase leading-tight tracking-tight" data-id="element-138">
            🚀 Podes tomar acción hoy con este sistema y ser{' '}
            <span className="text-hyrox-neon" data-id="element-139">otra persona en 12 semanas</span>{' '}
            o podes seguir igual que siempre.
          </p>
        </div>
      </section>
    </div>;
}